import { FORECAST_MIN_DAY, FORECAST_MAX_DAY, AUTH_FORCED_LAYER_NAME, FRANCE_COUNTRY_ID, SERVICE_LABELS, FORECAST_LINKED_SERVICES, DFMC_LAYER_NAME } from '../constants/accessRightsConfig';

// Initialise l'état d'un droit à partir du catalog_id et des droits utilisateur.
export const buildInitialPerm = (mod) => {
  const right       = mod.user_right;
  const serviceName = mod.services?.[0]?.name || '';
  const isAuth      = serviceName === 'auth';

  // Listes disponibles (null = pas de restriction)
  const availLayers  = mod.available_layer_ids?.length      > 0 ? mod.available_layer_ids      : null;
  const availCountry = mod.available_country_ids?.length    > 0 ? mod.available_country_ids    : null;
  const availDept    = mod.available_department_ids?.length > 0 ? mod.available_department_ids : null;

  // Intersection selected ∩ available
  const intersect = (ids, available) =>
    !available ? (ids || []) : (ids || []).filter(id => available.includes(id));

  let selectedLayerIds = intersect(right?.selected_layer_ids, availLayers);

  // Auth : forcer login_secufire toujours présent
  if (isAuth) {
    const forcedLayer = mod.layers?.find(l => l.name === AUTH_FORCED_LAYER_NAME);
    if (forcedLayer && !selectedLayerIds.includes(forcedLayer.id)) {
      selectedLayerIds = [...selectedLayerIds, forcedLayer.id];
    }
  }

  // Bornes disponibles pour les dates (historic)
  const availDateStart = mod.available_date_start ? new Date(mod.available_date_start) : null;
  const availDateEnd   = mod.available_date_end   ? new Date(mod.available_date_end)   : null;

  // Clamp date_start/date_end dans les bornes disponibles
  let dateStart = right?.date_start ? new Date(right.date_start) : null;
  let dateEnd   = right?.date_end   ? new Date(right.date_end)   : null;
  if (dateStart && availDateStart && dateStart < availDateStart) dateStart = availDateStart;
  if (dateStart && availDateEnd   && dateStart > availDateEnd)   dateStart = availDateEnd;
  if (dateEnd   && availDateStart && dateEnd   < availDateStart) dateEnd   = availDateStart;
  if (dateEnd   && availDateEnd   && dateEnd   > availDateEnd)   dateEnd   = availDateEnd;

  // Bornes disponibles pour l'intervalle météo
  const availMinJ = mod.available_interval_min_j ?? FORECAST_MIN_DAY;
  const availMaxJ = mod.available_interval_max_j ?? FORECAST_MAX_DAY;

  // Clamp intervalMinJ/MaxJ dans les bornes disponibles
  let intervalMinJ = right?.interval_min_j ?? 0;
  let intervalMaxJ = right?.interval_max_j ?? 0;
  intervalMinJ = Math.max(availMinJ, Math.min(0,       intervalMinJ));
  intervalMaxJ = Math.max(0,         Math.min(availMaxJ, intervalMaxJ));
 return {
    rightId:               mod.catalog_id,
    enabled:               isAuth ? true : (right?.is_active || right?.active || false),
    selectedLayerIds,
    selectedCountryIds:    intersect(right?.selected_country_ids,    availCountry),
    selectedDepartmentIds: intersect(right?.selected_department_ids, availDept),
    dateStart,
    dateEnd,
    intervalMinJ,
    intervalMaxJ,
  };
};

// Retrouve l'id de la couche DFMC dans un module display_layers_service.
export const getDfmcLayerId = (mod) =>
  mod?.layers?.find((l) => l.name === DFMC_LAYER_NAME)?.id ?? null;

// La couche DFMC est-elle sélectionnée pour ce module + ce perm ?
// "Aucune sélection" (selectedLayerIds vide) équivaut à "toutes les couches"
// dans le reste de l'app (cf. logique du bouton "Toutes") — donc DFMC est
// considérée sélectionnée dans ce cas aussi.
export const isDfmcLayerSelected = (mod, perm) => {
  const dfmcId = getDfmcLayerId(mod);
  if (dfmcId == null) return false;
  const selected = perm?.selectedLayerIds || [];
  return selected.length === 0 || selected.includes(dfmcId);
};

export const getModuleWarnings = (mod, perm) => {
  const warnings = [];
  if (!perm.enabled) return warnings;

  const serviceName     = mod.services?.[0]?.name || '';
  const isAuth           = serviceName === 'auth';
  const isWeather        = serviceName === 'weather';
  const isDisplayLayers  = serviceName === 'display_layers_service';
  const isHistoric       = serviceName === 'historic_fire_service';

  if (isAuth) return warnings; // module auth toujours forcé, rien à valider

  // ── Intervalle de prévision — météo toujours, couches terrain seulement
  // si la couche DFMC est sélectionnée ──
  const forecastApplies = isWeather || (isDisplayLayers && isDfmcLayerSelected(mod, perm));
  if (forecastApplies) {
    const minJ = perm.intervalMinJ ?? 0;
    const maxJ = perm.intervalMaxJ ?? 0;
    if (minJ === 0 && maxJ === 0) {
      warnings.push("Aucun intervalle de prévision défini — seule la journée actuelle (J0) sera accessible.");
    }
  }

  // ── Période d'accès (feux historiques) ──
  if (isHistoric) {
    if (!perm.dateStart || !perm.dateEnd) {
      warnings.push("Aucune période d'accès définie — aucune donnée historique ne sera accessible.");
    } else if (perm.dateStart > perm.dateEnd) {
      warnings.push("La date de début est postérieure à la date de fin — la période d'accès est invalide.");
    }
  }

  return warnings;
};

// ─────────────────────────────────────────────────────────────────────────
// getAllWarnings — agrège les avertissements de tous les modules concernés
// (utilisé par AccessRightsModal avant la sauvegarde)
// ─────────────────────────────────────────────────────────────────────────
export const getAllWarnings = (modules, permissions, catalogIds = null) => {
  const result = [];
  modules.forEach((mod) => {
    if (catalogIds && !catalogIds.has(mod.catalog_id)) return;
    const perm = permissions[mod.catalog_id];
    if (!perm) return;
    const moduleWarnings = getModuleWarnings(mod, perm);
    if (moduleWarnings.length > 0) {
      const label = SERVICE_LABELS[mod.services?.[0]?.name] || mod.services?.[0]?.name || `Module ${mod.catalog_id}`;
      result.push({ catalogId: mod.catalog_id, label, warnings: moduleWarnings });
    }
  });
  return result;
};

// ─────────────────────────────────────────────────────────────────────────
// Intervalle de prévision partagé — Météo ↔ Couches terrain (DFMC)
// ─────────────────────────────────────────────────────────────────────────

// Retrouve le catalog_id d'un module à partir de son nom de service.
const findCatalogIdByService = (modules, serviceName) =>
  modules.find((m) => m.services?.[0]?.name === serviceName)?.catalog_id ?? null;

// Si le module donné fait partie des services liés (FORECAST_LINKED_SERVICES),
// retourne le catalog_id de son "jumeau" — sinon null.
export const getLinkedForecastCatalogId = (modules, catalogId) => {
  const mod = modules.find((m) => m.catalog_id === catalogId);
  const serviceName = mod?.services?.[0]?.name;
  if (!FORECAST_LINKED_SERVICES.includes(serviceName)) return null;
  const otherService = FORECAST_LINKED_SERVICES.find((s) => s !== serviceName);
  return findCatalogIdByService(modules, otherService);
};

// Au chargement initial, "Météo" et "Couches terrain" peuvent avoir des
// valeurs d'intervalle différentes en base (historique, saisie séparée…).
// On les aligne sur celle qui est explicitement configurée (non "par défaut"
// 0/0) ; si les deux sont configurées, Météo est considérée comme la
// référence. Mute directement l'objet `perms` passé en argument.
export const syncLinkedForecastIntervals = (modules, perms) => {
  const [serviceA, serviceB] = FORECAST_LINKED_SERVICES; // weather, display_layers_service
  const idA = findCatalogIdByService(modules, serviceA);
  const idB = findCatalogIdByService(modules, serviceB);
  if (idA == null || idB == null) return perms;

  const permA = perms[idA];
  const permB = perms[idB];
  if (!permA || !permB) return perms;

  const aIsDefault = (permA.intervalMinJ ?? 0) === 0 && (permA.intervalMaxJ ?? 0) === 0;
  const bIsDefault = (permB.intervalMinJ ?? 0) === 0 && (permB.intervalMaxJ ?? 0) === 0;

  if (!aIsDefault) {
    permB.intervalMinJ = permA.intervalMinJ;
    permB.intervalMaxJ = permA.intervalMaxJ;
  } else if (!bIsDefault) {
    permA.intervalMinJ = permB.intervalMinJ;
    permA.intervalMaxJ = permB.intervalMaxJ;
  }

  return perms;
};